import { useState } from "react";
import { Plus, Check, X, ChevronRight, ChevronDown, Trash2 } from "lucide-react";

export function buildTree(nodes, parentId) {
  return nodes
    .filter(n => n.parent_id === parentId)
    .sort((a, b) => a.order - b.order)
    .map(n => ({ ...n, children: buildTree(nodes, n.id) }));
}

function flattenTree(nodes, level) {
  var out = [];
  for (var i = 0; i < nodes.length; i++) {
    out.push({ node: nodes[i], level: level });
    if (nodes[i]._expanded && nodes[i].children) {
      out = out.concat(flattenTree(nodes[i].children, level + 1));
    }
  }
  return out;
}

export function KnowledgeTreePanel({ tree, selectedNodeId, onSelect, onAddChild, onDelete }) {
  var [expandedIds, setExpandedIds] = useState({});
  var [addingFor, setAddingFor] = useState(null);
  var [newTitle, setNewTitle] = useState("");

  var hierarchy = buildTree(tree, null);

  function markExpanded(nodes) {
    return nodes.map(function(n) {
      var exp = expandedIds[n.id] !== undefined ? expandedIds[n.id] : (n.parent_id === null);
      return Object.assign({}, n, { _expanded: exp, children: markExpanded(n.children || []) });
    });
  }

  var marked = markExpanded(hierarchy);
  var flat = flattenTree(marked, 0);

  function toggle(id) {
    setExpandedIds(function(p) { var c = Object.assign({}, p); c[id] = !c[id]; return c; });
  }

  function doAdd(parentNode) {
    if (!newTitle.trim()) return;
    onAddChild(parentNode, newTitle.trim());
    setNewTitle("");
    setAddingFor(null);
  }

  return flat.map(function(item, idx) {
    var n = item.node;
    var lvl = item.level;
    var isSelected = selectedNodeId === n.id;
    var hasKids = n.children && n.children.length > 0;
    var isExp = n._expanded;

    return (
      <div key={n.id}>
        <div
          className={"group flex items-center gap-1 px-2 py-1.5 rounded-lg cursor-pointer transition-colors text-sm " + (isSelected ? "bg-[#A43790]/15 text-[#A43790] font-semibold" : "hover:bg-[#F0F1F5] text-[#3B3F5C]")}
          style={{ paddingLeft: lvl * 16 + 8 }}
          onClick={function() { onSelect(n.id); if (hasKids) toggle(n.id); }}
          data-testid={"tree-node-" + n.id}
        >
          {hasKids ? (isExp ? <ChevronDown size={14} className="shrink-0 text-[#6E7591]" /> : <ChevronRight size={14} className="shrink-0 text-[#6E7591]" />) : <span className="w-3.5 shrink-0" />}
          <span className="truncate flex-1">{n.title}</span>
          {onDelete && (
            <button
              className="opacity-0 group-hover:opacity-100 focus:opacity-100 p-0.5 rounded hover:bg-red-100 transition-opacity"
              onClick={function(e) { e.stopPropagation(); onDelete(n); }}
              title={n.parent_id === null ? "Vaciar los subtemas de esta área" : "Eliminar este tema y todos sus subtemas"}
              aria-label="Eliminar jerarquía"
              data-testid={"tree-delete-" + n.id}
            >
              <Trash2 size={12} className="text-red-500" />
            </button>
          )}
          <button
            className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-[#A43790]/10 transition-opacity"
            onClick={function(e) { e.stopPropagation(); setAddingFor(n.id); setNewTitle(""); }}
            data-testid={"tree-add-child-" + n.id}
          >
            <Plus size={12} className="text-[#A43790]" />
          </button>
        </div>
        {addingFor === n.id && (
          <div className="flex items-center gap-1 ml-2" style={{ paddingLeft: (lvl + 1) * 16 + 8 }}>
            <input autoFocus className="flex-1 text-xs border border-[#E1E5F0] rounded px-2 py-1 outline-none focus:border-[#A43790]" placeholder="Nombre del subtema" value={newTitle} onChange={function(e) { setNewTitle(e.target.value); }} onKeyDown={function(e) { if (e.key === "Enter") doAdd(n); if (e.key === "Escape") setAddingFor(null); }} data-testid={"tree-new-topic-input-" + n.id} />
            <button onClick={function() { doAdd(n); }} className="p-0.5 rounded hover:bg-green-100"><Check size={14} className="text-green-600" /></button>
            <button onClick={function() { setAddingFor(null); }} className="p-0.5 rounded hover:bg-red-100"><X size={14} className="text-red-500" /></button>
          </div>
        )}
      </div>
    );
  });
}
